// =============================================================================
// Ferramentas da área — a aba do Painel do Gestor (migration 072)
//
// Desenho fechado com o Enzo em 22/10:
//   · O gestor vê as ferramentas da área: custo, renovação, o confronto com o
//     orçamento e o que foi cobrado. Do cobrado, só a parte lançada no centro
//     de custo da área (`fn_ferramenta_cobrado_area`): o gestor não lê
//     lançamento de outra área.
//   · Pede ferramenta nova, cancelar uma da área, ou mudar plano ou licenças, e
//     acompanha aqui cada pedido. Quem decide é o Financeiro, em Solicitações.
//   · Fora isso, somente leitura: o cadastro é do Financeiro. Final do cartão,
//     login e senha não chegam aqui.
// =============================================================================

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  buscarFerramentasArea, buscarOrcamentoFerramentas, FalhaApi, pedirFerramenta,
  type Ferramenta, type PedidoFerramenta, type PedidoFerramentaEdicao, type TipoPedidoFerramenta,
} from "../lib/api.ts";
import { paraCentavos } from "../lib/dinheiro.ts";
import { competenciaCurta, moeda, percentual, type Tom } from "../lib/formato.ts";
import { CICLOS, type Ciclo } from "../lib/ferramentaCiclo.ts";
import { dolar } from "../lib/ferramentas.ts";
import {
  cobradoAreaPorMes, cobradoPorFerramenta, contraOrcamento, marcaCobranca, mesFechado, pct, resumir, ROTULO_FAMILIA, vivas,
} from "../lib/ferramentasAnalise.ts";
import { CabecalhoGrafico, COR, Kpi, LegendaGrafico, Painel, Pilula, Vazio } from "../componentes/ui.tsx";
import { CLASSE_BOTAO_BARRA } from "../componentes/Grade.tsx";
import { Modal } from "../componentes/Modal.tsx";
import { CobradoEstimado, ContraOrcamento } from "./FerramentasPainel.tsx";

const hojeSP = () => new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/Sao_Paulo", year: "numeric", month: "2-digit", day: "2-digit",
}).format(new Date());

const reais = (v: bigint) => moeda(v.toString());
const data = (d: string | null | undefined) => (d ? d.slice(0, 10).split("-").reverse().join("/") : "—");
const dataHora = (iso: string) => new Date(iso).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" });
const rotuloCiclo = (c: Ciclo | null) => CICLOS.find((x) => x.valor === c)?.rotulo ?? "—";
const valorNaMoeda = (m: "BRL" | "USD" | null, v: string | null) => (v === null ? "—" : m === "USD" ? dolar(v) : moeda(v));

export const ROTULO_TIPO_PEDIDO: Record<TipoPedidoFerramenta, string> = {
  nova: "Ferramenta nova",
  cancelar: "Cancelar",
  alterar: "Mudar plano ou licenças",
};

/** A situação do pedido, como o gestor lê. */
export function situacaoPedido(p: PedidoFerramenta): { texto: string; tom: Tom } {
  if (p.estado === "aberto") return { texto: "Aguardando o Financeiro", tom: "atencao" };
  if (p.estado === "encaminhado") return { texto: `Encaminhado a ${p.encaminhado_a}`, tom: "atencao" };
  if (p.estado === "aprovado") return { texto: p.aprovado_por ? `Aprovado por ${p.aprovado_por}` : "Aprovado", tom: "ok" };
  return { texto: "Recusado", tom: "risco" };
}

export function FerramentasArea() {
  const q = useQuery({ queryKey: ["ferramentas-area"], queryFn: buscarFerramentasArea });
  const [ccId, setCcId] = useState<string | null>(null);
  const [pedido, setPedido] = useState<{ tipo: TipoPedidoFerramenta; ferramenta?: Ferramenta } | null>(null);
  const hoje = hojeSP();
  const anoAtual = Number(hoje.slice(0, 4));
  const [ano, setAno] = useState(anoAtual);
  const orc = useQuery({ queryKey: ["ferramentas-orcamento", ano], queryFn: () => buscarOrcamentoFerramentas(ano) });

  const area = q.data?.areas.find((a) => a.id === ccId) ?? q.data?.areas[0];
  const todas = useMemo(() => (q.data?.ferramentas ?? []).filter((f) => f.centro_custo_id === area?.id), [q.data, area]);
  const fs = useMemo(() => vivas(todas), [todas]);
  const pedidos = useMemo(() => (q.data?.pedidos ?? []).filter((p) => p.centro_custo_id === area?.id), [q.data, area]);
  const resumo = useMemo(() => resumir(fs, hoje), [fs, hoje]);
  const fechado = mesFechado(hoje);
  // O cobrado lançado na área: o de cada ferramenta e o do grupo do fornecedor (073).
  const serie = useMemo(() => (area ? cobradoAreaPorMes(q.data?.cobrado ?? [], area.id, fechado) : []), [q.data, area, fechado]);
  const doMes = useMemo(() => (area ? cobradoPorFerramenta(q.data?.cobrado ?? [], area.id, fechado) : new Map()), [q.data, area, fechado]);
  const contraOrc = useMemo(() => contraOrcamento(fs, orc.data?.linhas ?? []), [fs, orc.data]);

  if (q.isPending) return <div className="pagina py-6 text-[13px] text-tinta-fraca">Carregando…</div>;
  if (q.isError) return <div className="pagina py-6"><Vazio titulo="Não foi possível carregar" dica={(q.error as FalhaApi).message} /></div>;
  if (!area) {
    return (
      <div className="pagina py-6">
        <Vazio titulo="Seu usuário não está vinculado a um centro de custo" dica="Peça ao Financeiro o vínculo com a sua área." />
      </div>
    );
  }
  const canceladas = todas.length - fs.length;
  const abertos = pedidos.filter((p) => p.estado === "aberto" || p.estado === "encaminhado").length;

  return (
    <div className="pagina grid gap-5 pb-20">
      <header className="flex flex-wrap items-end justify-between gap-3 pt-7">
        <div>
          <p className="text-[11px] font-semibold tracking-[0.11em] text-tinta-fraca uppercase">Ferramentas e assinaturas</p>
          <h1 className="mt-1 font-display text-[22px] leading-tight font-bold tracking-tight">Ferramentas da área</h1>
          <p className="mt-1 max-w-3xl text-[14px] text-tinta-fraca">
            O que a área assina, quanto custa e quando renova. Para contratar, cancelar ou mudar o plano, faça o pedido aqui:
            quem decide é o Financeiro.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          {q.data.areas.length > 1 && (
            <label className="text-[12px] font-semibold text-tinta-media">Área
              <select value={area.id} onChange={(e) => setCcId(e.target.value)}
                className="mt-1 block rounded-lg border border-linha bg-superficie px-3 py-1.5 text-[13px]">
                {q.data.areas.map((a) => <option key={a.id} value={a.id}>{a.codigo} · {a.nome}</option>)}
              </select>
            </label>
          )}
          <button type="button" onClick={() => setPedido({ tipo: "nova" })}
            className="rounded-lg bg-laranja px-4 py-2 text-[13px] font-bold text-azul">Pedir ferramenta</button>
        </div>
      </header>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-3">
        <Kpi borda="topo" rotulo="Ferramentas" valor={String(resumo.ativas)}
          apoio={canceladas ? `${canceladas} cancelada(s) fora da conta` : "ativas na área"} />
        <Kpi borda="topo" destaque rotulo="Custo por mês" valor={reais(resumo.mensal)} apoio={`${reais(resumo.anual)} no ano`}
          dica="O valor do ciclo de cada ferramenta ÷ os meses do ciclo; em dólar, pela PTAX mais o acréscimo do cartão." />
        <Kpi borda="topo" rotulo="Em dólar" valor={percentual(pct(resumo.mensalUsd, resumo.mensal))} apoio={`${reais(resumo.mensalUsd)} por mês`}
          dica="A parte do custo cobrada em dólar: a que o câmbio mexe." />
        <Kpi borda="topo" rotulo="Renovam em 60 dias" valor={String(resumo.renovacoes60.n)} apoio={reais(resumo.renovacoes60.valor)}
          tom={resumo.renovacoes60.n ? "atencao" : undefined}
          dica="As que têm a próxima renovação nos próximos 60 dias. Para não renovar, peça o cancelamento antes." />
        <Kpi borda="topo" rotulo="Pedidos em aberto" valor={String(abertos)} tom={abertos ? "atencao" : undefined}
          apoio="esperando o Financeiro" />
      </div>

      <Pedidos pedidos={pedidos} />

      <div className="grid gap-5">
        <Painel>
          <CabecalhoGrafico titulo="Cobrado × estimado"
            dica="O que o Conta Azul cobrou das ferramentas da área, mês a mês (com o IOF), contra o custo por mês de hoje. Só o que foi lançado no centro de custo da área: cobrança lançada em outra área não aparece aqui." />
          <CobradoEstimado serie={serie} estimado={resumo.mensal} />
          <LegendaGrafico series={[["Cobrado no mês", COR.realizado], ["Estimado por mês (hoje)", COR.forecast]]} />
        </Painel>
        <Painel>
          <CabecalhoGrafico titulo="Ferramentas × orçamento"
            dica="O custo das ferramentas no ano contra o orçado da área na mesma conta, e o que já foi realizado nela (tudo o que foi lançado na conta, não só ferramenta)."
            acessorio={
              <select value={ano} onChange={(e) => setAno(Number(e.target.value))} aria-label="Exercício"
                className="rounded-lg border border-linha bg-superficie px-2 py-1 text-[12.5px]">
                {[anoAtual - 1, anoAtual, anoAtual + 1].map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            } />
          <ContraOrcamento linhas={contraOrc.linhas} semCoordenada={contraOrc.semCoordenada} carregando={orc.isPending}
            erro={orc.isError ? (orc.error as FalhaApi).message : null} ano={ano} />
        </Painel>
      </div>

      <Painel>
        <CabecalhoGrafico titulo="As ferramentas da área"
          dica={`O cobrado é o de ${competenciaCurta(`${fechado}-01`)}, o último mês fechado, só o lançado na área. Quando a cobrança vem de um fornecedor que várias ferramentas dividem, ela aparece como do grupo, com o total. A marca aparece nas que cobram todo mês.`} />
        {fs.length === 0 ? <Vazio titulo="Nenhuma ferramenta ativa nesta área" dica="Para contratar uma, use Pedir ferramenta." /> : (
          <div className="thin-scroll overflow-x-auto">
            <table className="w-full min-w-[860px] text-[12.5px]">
              <thead className="text-left text-[11px] text-tinta-fraca">
                <tr>
                  <th className="px-2 pb-1.5">Ferramenta</th><th className="px-2 pb-1.5">Periodicidade</th>
                  <th className="px-2 pb-1.5 text-right">Por mês</th><th className="px-2 pb-1.5">Responsável</th>
                  <th className="px-2 pb-1.5">Renova em</th>
                  <th className="px-2 pb-1.5 text-right">Cobrado em {competenciaCurta(`${fechado}-01`)}</th><th className="px-2 pb-1.5" />
                </tr>
              </thead>
              <tbody>
                {[...fs].sort((a, b) => Number(BigInt(b.custo_mensal_centavos ?? "0") - BigInt(a.custo_mensal_centavos ?? "0"))).map((f) => {
                  const cobrado = doMes.get(f.id);
                  const marca = marcaCobranca(f, cobrado);
                  const pendente = pedidos.some((p) => p.ferramenta_id === f.id && (p.estado === "aberto" || p.estado === "encaminhado"));
                  return (
                    <tr key={f.id} className="border-t border-linha align-top">
                      <td className="px-2 py-1.5 pr-2">
                        <b>{f.nome}</b>
                        <span className="block text-[11px] text-tinta-fraca">
                          {ROTULO_FAMILIA[f.familia ?? "outros"]}{f.status === "nova" ? " · recém-contratada" : ""}{f.teste ? " · em teste" : ""}
                        </span>
                      </td>
                      <td className="px-2 py-1.5">{rotuloCiclo(f.ciclo)}{f.moeda === "USD" && <span className="text-tinta-fraca"> · US$</span>}</td>
                      <td className="num px-2 py-1.5 text-right font-semibold">{f.custo_mensal_centavos ? moeda(f.custo_mensal_centavos) : "—"}</td>
                      <td className="px-2 py-1.5">{f.responsavel ?? <span className="text-tinta-fraca">—</span>}</td>
                      <td className="px-2 py-1.5">{data(f.proxima_renovacao)}</td>
                      <td className="num px-2 py-1.5 text-right">
                        {cobrado?.proprio ? reais(cobrado.proprio) : cobrado?.grupo ? "" : "—"}
                        {cobrado?.grupo && (
                          <span className="block text-[11px] text-tinta-fraca" title={`O fornecedor "${cobrado.grupo.chave}" cobra ${cobrado.grupo.ferramentas} ferramenta(s) da área juntas: o total não se separa por ferramenta.`}>
                            no grupo {cobrado.grupo.chave}: {reais(cobrado.grupo.total)}
                          </span>
                        )}
                        {marca === "acima" && <span className="block text-[11px] text-risco-tinta">acima do estimado</span>}
                        {marca === "sem_lancamento" && <span className="block text-[11px] text-atencao-tinta">nada lançado na área</span>}
                      </td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap">
                        {pendente ? <span className="text-[11.5px] text-tinta-fraca">pedido em aberto</span> : (
                          <span className="flex justify-end gap-1">
                            <button type="button" className={CLASSE_BOTAO_BARRA} onClick={() => setPedido({ tipo: "alterar", ferramenta: f })}>Mudar plano</button>
                            <button type="button" className={CLASSE_BOTAO_BARRA} onClick={() => setPedido({ tipo: "cancelar", ferramenta: f })}>Cancelar</button>
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Painel>

      {pedido && (
        <ModalPedido area={area} ferramentas={fs} inicial={pedido} onFechar={() => setPedido(null)} />
      )}
    </div>
  );
}

function Pedidos({ pedidos }: { pedidos: PedidoFerramenta[] }) {
  if (pedidos.length === 0) return null;
  return (
    <Painel>
      <CabecalhoGrafico titulo="Pedidos da área" dica="Os pedidos de ferramenta e a situação de cada um. Quem decide é o Financeiro; quando ele encaminha a outro aprovador, aparece aqui a quem." />
      <ul className="grid gap-2 text-[12.5px]">
        {pedidos.slice(0, 15).map((p) => {
          const s = situacaoPedido(p);
          return (
            <li key={p.id} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-t border-linha pt-2 first:border-t-0 first:pt-0">
              <span>
                <b>{ROTULO_TIPO_PEDIDO[p.tipo]}</b> · {p.ferramenta_nome}
                <span className="text-tinta-fraca"> · pedido em {dataHora(p.criado_em)}{p.solicitante_nome ? ` por ${p.solicitante_nome}` : ""}</span>
                {p.estado === "recusado" && p.parecer && <span className="block text-[11.5px] text-risco-tinta">{p.parecer}</span>}
                {p.estado === "aprovado" && p.tipo === "nova" && <span className="block text-[11.5px] text-tinta-fraca">Já está no cadastro; o Financeiro completa conta e cartão.</span>}
              </span>
              <Pilula tom={s.tom}>{s.texto}</Pilula>
            </li>
          );
        })}
      </ul>
    </Painel>
  );
}

function ModalPedido({ area, ferramentas, inicial, onFechar }: {
  area: { id: string; codigo: string; nome: string };
  ferramentas: Ferramenta[];
  inicial: { tipo: TipoPedidoFerramenta; ferramenta?: Ferramenta };
  onFechar: () => void;
}) {
  const qc = useQueryClient();
  const [tipo, setTipo] = useState<TipoPedidoFerramenta>(inicial.tipo);
  const [ferramentaId, setFerramentaId] = useState(inicial.ferramenta?.id ?? "");
  const atual = ferramentas.find((f) => f.id === ferramentaId);
  const [nome, setNome] = useState("");
  const [motivo, setMotivo] = useState("");
  const [responsavel, setResponsavel] = useState("");
  const [ciclo, setCiclo] = useState<Ciclo | "">(inicial.ferramenta?.ciclo ?? "");
  const [moedaPedido, setMoedaPedido] = useState<"BRL" | "USD">(inicial.ferramenta?.moeda ?? "BRL");
  const [valor, setValor] = useState("");
  const [aPartirDe, setAPartirDe] = useState("");
  const [teste, setTeste] = useState(false);
  const [url, setUrl] = useState("");

  const centavos = valor.trim() ? paraCentavos(valor) : null;
  const pedeValor = tipo !== "cancelar";
  const falta =
    (tipo === "nova" && !nome.trim()) ? "o nome da ferramenta"
    : (tipo !== "nova" && !ferramentaId) ? "a ferramenta"
    : motivo.trim().length < 5 ? (tipo === "nova" ? "para que serve" : tipo === "cancelar" ? "por que cancelar" : "o que muda")
    : (pedeValor && !ciclo) ? "a periodicidade"
    : (pedeValor && !centavos) ? "o valor"
    : null;

  const enviar = useMutation({
    mutationFn: () => {
      const p: PedidoFerramentaEdicao = { tipo, motivo: motivo.trim() };
      if (tipo === "nova") Object.assign(p, { centro_custo_id: area.id, nome: nome.trim(), responsavel: responsavel.trim() || undefined, teste, url: url.trim() || undefined });
      else p.ferramenta_id = ferramentaId;
      if (pedeValor) Object.assign(p, { ciclo: ciclo as Ciclo, moeda: moedaPedido, valor_ciclo_centavos: centavos! });
      if (aPartirDe) p.a_partir_de = aPartirDe;
      return pedirFerramenta(p);
    },
    onSuccess: () => {
      for (const k of ["ferramentas-area", "contadores", "avisos"]) void qc.invalidateQueries({ queryKey: [k] });
      onFechar();
    },
  });

  const campo = "mt-1 block w-full rounded-lg border border-linha bg-superficie px-3 py-1.5 text-[13px]";
  const rotulo = "block text-[12px] font-semibold text-tinta-media";
  const rotuloValor = CICLOS.find((c) => c.valor === ciclo)?.campo ?? "Valor";

  return (
    <Modal titulo={`Pedir ao Financeiro · ${area.codigo} ${area.nome}`} onFechar={onFechar} largura="max-w-xl">
      <div className="grid gap-3">
        <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="O que pedir">
          {(["nova", "cancelar", "alterar"] as const).map((t) => (
            <button key={t} type="button" role="radio" aria-checked={tipo === t} onClick={() => setTipo(t)}
              className={`rounded-lg border px-3 py-1.5 text-[12.5px] font-semibold ${tipo === t ? "border-laranja bg-laranja/10 text-tinta" : "border-linha text-tinta-media"}`}>
              {ROTULO_TIPO_PEDIDO[t]}
            </button>
          ))}
        </div>

        {tipo === "nova" ? (
          <label className={rotulo}>Nome da ferramenta
            <input value={nome} onChange={(e) => setNome(e.target.value)} className={campo} maxLength={200} />
          </label>
        ) : (
          <label className={rotulo}>Ferramenta
            <select value={ferramentaId} onChange={(e) => {
              setFerramentaId(e.target.value);
              const f = ferramentas.find((x) => x.id === e.target.value);
              if (f) { setCiclo(f.ciclo ?? ""); setMoedaPedido(f.moeda); }
            }} className={campo}>
              <option value="">Escolha…</option>
              {ferramentas.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
            </select>
          </label>
        )}
        {atual && tipo === "alterar" && (
          <p className="rounded-lg bg-superficie-2 px-3 py-2 text-[12px] text-tinta-media">
            Hoje: {rotuloCiclo(atual.ciclo)}, {valorNaMoeda(atual.moeda, atual.valor_ciclo_centavos)} por ciclo
            {atual.custo_mensal_centavos && <> ({moeda(atual.custo_mensal_centavos)} por mês)</>}.
          </p>
        )}

        <label className={rotulo}>{tipo === "nova" ? "Para que serve" : tipo === "cancelar" ? "Por que cancelar" : "O que muda (por exemplo, mais duas licenças)"}
          <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3} className={campo} maxLength={2000} />
        </label>

        {pedeValor && (
          <div className="grid gap-3 sm:grid-cols-3">
            <label className={rotulo}>Periodicidade
              <select value={ciclo} onChange={(e) => setCiclo(e.target.value as Ciclo)} className={campo}>
                <option value="">Escolha…</option>
                {CICLOS.map((c) => <option key={c.valor} value={c.valor}>{c.rotulo}</option>)}
              </select>
            </label>
            <label className={rotulo}>Moeda
              <select value={moedaPedido} onChange={(e) => setMoedaPedido(e.target.value as "BRL" | "USD")} className={campo}>
                <option value="BRL">Real</option>
                <option value="USD">Dólar</option>
              </select>
            </label>
            <label className={rotulo}>{tipo === "alterar" ? `${rotuloValor} novo` : rotuloValor}
              <input value={valor} onChange={(e) => setValor(e.target.value)} inputMode="decimal" placeholder="0,00" className={`${campo} num`} />
            </label>
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <label className={rotulo}>{tipo === "cancelar" ? "Cancelar a partir de" : "A partir de"}
            <input type="date" value={aPartirDe} onChange={(e) => setAPartirDe(e.target.value)} className={campo} />
          </label>
          {tipo === "nova" && (
            <label className={rotulo}>Responsável
              <input value={responsavel} onChange={(e) => setResponsavel(e.target.value)} className={campo} maxLength={200} />
            </label>
          )}
        </div>
        {tipo === "nova" && (
          <>
            <label className={rotulo}>Site
              <input value={url} onChange={(e) => setUrl(e.target.value)} className={campo} placeholder="https://" maxLength={500} />
            </label>
            <label className="flex items-center gap-2 text-[12.5px] text-tinta-media">
              <input type="checkbox" checked={teste} onChange={(e) => setTeste(e.target.checked)} /> Começa em teste gratuito
            </label>
          </>
        )}

        {enviar.isError && <p className="rounded-lg border border-risco-linha bg-risco-bg px-3 py-2 text-[12.5px] text-risco-tinta">{(enviar.error as FalhaApi).message}</p>}
        <div className="flex items-center justify-end gap-2">
          {falta && <span className="mr-auto text-[12px] text-tinta-fraca">Falta {falta}.</span>}
          <button type="button" onClick={onFechar} className={CLASSE_BOTAO_BARRA}>Voltar</button>
          <button type="button" disabled={!!falta || enviar.isPending} onClick={() => enviar.mutate()}
            className="rounded-lg bg-laranja px-4 py-2 text-[13px] font-bold text-azul disabled:cursor-not-allowed disabled:opacity-45">
            {enviar.isPending ? "Enviando…" : "Enviar ao Financeiro"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
